import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Grid,
    Divider,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';

const formatAccountHeadForDisplay = (head) => {
    if (!head) return '';
    const words = head.trim().replace(/\./g, '').split(" ");
    return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
};

// Function to remove duplicates while preserving original values
const removeDuplicateAccountHeads = (accountHeads) => {
    const uniqueHeads = [];
    const displayMap = new Map();
    const duplicatesMap = new Map();

    accountHeads.forEach(head => {
        const formattedDisplay = formatAccountHeadForDisplay(head).toLowerCase();

        if (!displayMap.has(formattedDisplay)) {
            displayMap.set(formattedDisplay, head);
            uniqueHeads.push(head);
            duplicatesMap.set(formattedDisplay, 1);
        } else {
            duplicatesMap.set(formattedDisplay, duplicatesMap.get(formattedDisplay) + 1);
        }
    });

    return { uniqueHeads, duplicatesMap };
};

const SDESelection = ({
    expenseHeads,
    onSelectionChange,
    loading,
    useExpenseMappedItems = false,
    accountHeadMappings = null
}) => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [uniqueExpenseHeads, setUniqueExpenseHeads] = useState([]);
    const [duplicatesMap, setDuplicatesMap] = useState(new Map());
    const [displayToOriginalMap, setDisplayToOriginalMap] = useState({});

    useEffect(() => {
        if (expenseHeads && expenseHeads.length > 0) {
            // Extract merged headers to filter them out
            const mergedHeaders = [];
            if (accountHeadMappings) {
                Object.keys(accountHeadMappings).forEach(head => {
                    // Check if this is a head that has been merged with another (has a conflictHeader)
                    if (typeof accountHeadMappings[head] === 'object' &&
                        accountHeadMappings[head].conflictHeader) {
                        // This head should be filtered out
                        mergedHeaders.push(head.toLowerCase());
                    }
                });
            }

            // Filter out any heads marked as 'hide' or merged with others
            const filteredHeads = expenseHeads.filter(head => {
                const headLower = head.toLowerCase();

                // Filter out merged source headers
                if (mergedHeaders.includes(headLower)) {
                    return false;
                }

                // Filter out hidden headers
                if (accountHeadMappings) {
                    if (typeof accountHeadMappings[headLower] === 'object') {
                        return accountHeadMappings[headLower].category !== 'hide';
                    }
                    return !accountHeadMappings[headLower] || accountHeadMappings[headLower] !== 'hide';
                }

                return true;
            });

            // Process expense heads to create a map from formatted display to all original values
            const formattedToOriginalMap = {};

            filteredHeads.forEach(head => {
                const formatted = formatAccountHeadForDisplay(head).toLowerCase();

                if (!formattedToOriginalMap[formatted]) {
                    formattedToOriginalMap[formatted] = [];
                }

                formattedToOriginalMap[formatted].push(head);
            });

            setDisplayToOriginalMap(formattedToOriginalMap);

            // Remove duplicates for display
            const { uniqueHeads, duplicatesMap } = removeDuplicateAccountHeads(filteredHeads);
            setUniqueExpenseHeads(uniqueHeads);
            setDuplicatesMap(duplicatesMap);

            // When using expense-mapped items, select all items by default
            if (useExpenseMappedItems) {
                setSelectedItems([...filteredHeads]);

                if (onSelectionChange) {
                    onSelectionChange([...filteredHeads]);
                }
            } else {
                // Start with an empty selection otherwise
                setSelectedItems([]);
                if (onSelectionChange) {
                    onSelectionChange([]);
                }
            }
        }
    }, [expenseHeads, useExpenseMappedItems, accountHeadMappings]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ ml: 2 }}>
                    Loading expense categories...
                </Typography>
            </Box>
        );
    }

    if (!expenseHeads || expenseHeads.length === 0) {
        return (
            <Alert severity="warning">
                {useExpenseMappedItems
                    ? "No expense categories were mapped in the previous step. Please go back and categorize at least one item as 'Expense'."
                    : "No expense categories found in the uploaded data."
                }
            </Alert>
        );
    }

    const handleToggleItem = (displayedHead) => {
        const formattedDisplay = formatAccountHeadForDisplay(displayedHead).toLowerCase();
        const originalValues = displayToOriginalMap[formattedDisplay] || [displayedHead];

        // Check if any of these original values are currently selected
        const anySelected = originalValues.some(val => selectedItems.includes(val));

        let newSelection;

        if (anySelected) {
            // If any are selected, remove all of them
            newSelection = selectedItems.filter(item => !originalValues.includes(item));
        } else {
            // If none are selected, add all of them
            newSelection = [...selectedItems, ...originalValues];
        }

        setSelectedItems(newSelection);

        // Notify parent component of selection change
        if (onSelectionChange) {
            onSelectionChange(newSelection);
        }
    };

    const handleSelectAll = () => {
        // Filter out hidden items
        const visibleHeads = accountHeadMappings
            ? expenseHeads.filter(head => {
                const headLower = head.toLowerCase();
                return !accountHeadMappings[headLower] || accountHeadMappings[headLower] !== 'hide';
            })
            : expenseHeads;

        setSelectedItems([...visibleHeads]);
        if (onSelectionChange) {
            onSelectionChange([...visibleHeads]);
        }
    };

    const handleSelectNone = () => {
        setSelectedItems([]);
        if (onSelectionChange) {
            onSelectionChange([]);
        }
    };

    const handleSelectDefault = () => {
        // Only use this for non-expense-mapped items
        if (useExpenseMappedItems) {
            return handleSelectAll();
        }

        setSelectedItems([]);
        if (onSelectionChange) {
            onSelectionChange([]);
        }
    };

    // Function to check if an item is selected (checking all original values)
    const isItemSelected = (displayedHead) => {
        const formattedDisplay = formatAccountHeadForDisplay(displayedHead).toLowerCase();
        const originalValues = displayToOriginalMap[formattedDisplay] || [displayedHead];

        return originalValues.some(val => selectedItems.includes(val));
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAll}
                    >
                        Select All
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectNone}
                    >
                        Select None
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    maxHeight: 350,
                    overflow: 'auto',
                    backgroundColor: theme => theme.palette.background.paper
                }}
            >
                <Grid container spacing={1}>
                    {uniqueExpenseHeads.map((item, index) => {
                        const displayItem = formatAccountHeadForDisplay(item);
                        const formattedLower = displayItem.toLowerCase();
                        const duplicateCount = (duplicatesMap.get(formattedLower) || 1);
                        const hasDuplicates = duplicateCount > 1;

                        return (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Tooltip
                                    title={hasDuplicates ? `${duplicateCount} identical entries will be selected` : ""}
                                    placement="top"
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isItemSelected(item)}
                                                onChange={() => handleToggleItem(item)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                {displayItem}
                                                {hasDuplicates && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        ({duplicateCount} entries)
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </Tooltip>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Selected {selectedItems.length} of {uniqueExpenseHeads.length} items
                </Typography>
            </Box>
        </Box>
    );
};

export default SDESelection;